import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { STORAGE, sessionStorageKeys } from 'config/storage-config';
import {
  ACCEPTED_PAYMENT_METHOD,
  PARTNER_CREDIT_VALUES,
  PARTNER_MOBILE_LOGO,
  PARTNER_MACHINES,
  LIDL_COOKIE
} from 'config/app-config';
import {
  PARTNERS,
  PARTNER_LOGO,
  DIRECT_DEBIT_VERIFICATION_MAX_ATTEMPTS,
  AGENT_JOURNEYS_DIRECT_DEBIT_MAX_ATTEMPTS
} from 'config/app-config';
import { getSessionStoragePartner } from 'utils/getSessionStoragePartner';
import { checkMatchPath } from 'utils/misc';
import { useResponsive } from 'hooks/useResponsive';
import { directSalesMachineConfig } from 'machines/direct-sales-machine';
import { directSalesMachineConfig as directSalesMachineConfigRevamp } from 'machines/direct-sales-machine-revamp';

const findPartnerLanding = ({ pathname, search }) => {
  const partnerLandingPaths = Object.values(PARTNERS);
  const matchesQuery = partnerLandingPaths.some((partner) =>
    checkMatchPath(pathname + search, partner)
  );
  const matchesPath = partnerLandingPaths.some((partner) =>
    checkMatchPath(pathname, partner)
  );

  switch (true) {
    case matchesQuery:
      return pathname + search;
    case matchesPath:
      return pathname;
    default:
      return null;
  }
};

const useMachineConfig = (machine) => {
  const { releaseRevampJourney } = useFlags();

  if (releaseRevampJourney && machine.id === directSalesMachineConfig.id) {
    return directSalesMachineConfigRevamp;
  }

  return machine;
};

export const usePartner = () => {
  const location = useLocation();

  const { isDesktop } = useResponsive();
  const { state } = location;
  const partnerLandingPaths = Object.values(PARTNERS);
  const partnerLanding = findPartnerLanding(location);

  // For browser back from DS journey to any previous partner page except landing page
  // restore partner from browser history saved before leaving the partner journey
  // See: useHistoryNavigation
  const shouldRestorePartner = !!state?.partnerName;

  // restore partner from sessionStorage if it exists. See useExitApp saving partner before exiting
  if (shouldRestorePartner) {
    STORAGE.setItem(sessionStorageKeys.PARTNER_PATH, state?.partnerName, false);
  }

  if (partnerLanding) {
    STORAGE.setItem(sessionStorageKeys.PARTNER_PATH, partnerLanding, false);
  }

  const partnerPath = getSessionStoragePartner();

  const activePartner = partnerLandingPaths.reduce((acc, partner) => {
    return {
      ...acc,
      [partner]: checkMatchPath(partnerPath, partner)
    };
  }, {});

  const isLidl = !!Cookies.get(LIDL_COOKIE);
  const isDSJourney =
    activePartner[PARTNERS.DIRECT_SALES] ||
    // treat these journey as standard journeyes
    activePartner[PARTNERS.QUOTE_BY_EMAIL] ||
    activePartner[PARTNERS.AWIN] ||
    activePartner[PARTNERS.REFER_A_FRIEND] ||
    isLidl;

  const currentPartner = Object.entries(activePartner).filter(
    ([, val]) => !!val
  )[0];

  const partnerName = currentPartner ? currentPartner[0] : null;
  const creditValues = PARTNER_CREDIT_VALUES[partnerName] || [];
  const partnerLogo = isDesktop
    ? PARTNER_LOGO[partnerName]
    : PARTNER_MOBILE_LOGO[partnerName];
  const acceptedPaymentMethods =
    ACCEPTED_PAYMENT_METHOD[partnerName] ??
    ACCEPTED_PAYMENT_METHOD[PARTNERS.DIRECT_SALES];
  const stateMachineConfig = useMachineConfig(
    PARTNER_MACHINES[partnerName] || directSalesMachineConfig
  );

  const ddMaxAttemps =
    AGENT_JOURNEYS_DIRECT_DEBIT_MAX_ATTEMPTS[partnerName] ||
    DIRECT_DEBIT_VERIFICATION_MAX_ATTEMPTS;

  return {
    partnerName,
    partnerPath,
    partnerLogo,
    activePartner,
    isDSJourney,
    isLidl,
    creditValues,
    acceptedPaymentMethods,
    stateMachineConfig,
    ddMaxAttemps
  };
};
